// Lib
import { Else, FieldType } from "@shoothill/core";
import { action, computed, observable } from "mobx";
import validator from "validator";
import isLandLine from "Globals/validators/LandLine";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";

//App
import { AppUrls } from "AppUrls";

//Models
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";
import { EmploymentStatusEnum } from "Globals/Models/EmploymentStatusEnum";
import { EmploymentDetailsModel } from "Globals/Models/Domain/FormSections/EmploymentDetails/EmploymentDetailsModel";
import { EmploymentDetailsDTO } from "Globals/Models/DTO/EmploymentDetailsDTO";
import { UserFile } from "Globals/Models/UserFile";

// View Models
import FormSectionViewModelBase from "Globals/ViewModels/FormSections/FormSectionViewModelBase";
import ArmedForcesProofViewModel from "Globals/ViewModels/FormSections/EmploymentDetails/ArmedForcesProofViewModel";
import AuditedAccountViewModel from "Globals/ViewModels/FormSections/EmploymentDetails/AuditedAccountViewModel";

export default class EmploymentDetailsViewModel extends FormSectionViewModelBase<EmploymentDetailsModel> {
    @observable public isSectionExpanded: boolean = true; // TODO Link up with accordian
    @observable public armedForcesProofViewModels: ArmedForcesProofViewModel[] = [];
    @observable public auditedAccountViewModels: AuditedAccountViewModel[] = [];
    constructor(employmentDetailsModel: EmploymentDetailsModel, _updateSectionCompleted: (index: FormSectionsEnum, value: boolean) => void) {
        super(_updateSectionCompleted, employmentDetailsModel);
        this.saveStore.addCallback(FormSectionsEnum.EmploymentDetails, this.save);
        this.saveFunction = this.save; // This is the save function that will be used by the form section base to auto save
        this.createViewModels();
        if (this.updateSectionCompleted) {
            this.updateSectionCompleted(FormSectionsEnum.EmploymentDetails, this.canProceed);
        }
    }
    /**
     * Each form section is responsiable for its own saving.
     * This function is given to the super class in the constructor.
     */
    public save = async (): Promise<void> => {
        try {
            let isMyModelValid: boolean = await this.isMyModelValid();
            if (isMyModelValid && this.isModelDirty) {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                let upsertEmploymentDetails: EmploymentDetailsDTO = this.model.toDto();
                const apiResult = await this.Post<EmploymentDetailsDTO>(AppUrls.Server.ApplicationForm.Upsert.EmploymentDetails, upsertEmploymentDetails);

                if (apiResult.wasSuccessful) {
                    this.model.fromEmploymentDetailsDto(apiResult.payload);
                    console.info("EmploymentDetails:Upsert-Successful");
                    // Will set this section as completed when canProceed returns true
                    this.updateSectionCompleted(FormSectionsEnum.EmploymentDetails, this.canProceed);

                    // Loop through stash and commit to DB.
                    let childForms = this.armedForcesProofViewModels.filter((a) => !a.model.employmentDetailId);
                    childForms.map(async (c) => {
                        await c.saveFromStash(apiResult.payload.id as string);
                    });

                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("EmploymentDetails:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            console.log("Error:", exception);
        } finally {
            this.setIsLoading(false);
            this.saveStore.setIsSaving(false);
            this.saveStore.setIsSaved(true);
        }
    };

    @computed
    public get getSortedArmedForcesProofViewModel(): ArmedForcesProofViewModel[] {
        return this.armedForcesProofViewModels
            .slice()
            .sort((a, b) => (b.model.createdDate ? new Date(b.model.createdDate).getTime() : 0) - (a.model.createdDate ? new Date(a.model.createdDate).getTime() : 0));
    }
    @computed
    public get getArmedForcesProofUserFiles(): UserFile[] {
        let userFiles: UserFile[] = [];
        this.getSortedArmedForcesProofViewModel.forEach((armedForcesProof) => {
            userFiles.push({
                id: armedForcesProof.model.id,
                documentTypeId: "",
                fileName: armedForcesProof.model.fileName,
                fileUrl: armedForcesProof.model.fileUrl,
            });
        });
        return userFiles;
    }
    @computed
    public get getSortedAuditedAccountViewModel(): AuditedAccountViewModel[] {
        return this.auditedAccountViewModels.sort(
            (a, b) => (b.model.createdDate ? new Date(b.model.createdDate).getTime() : 0) - (a.model.createdDate ? new Date(a.model.createdDate).getTime() : 0),
        );
    }
    @computed
    public get getAuditedAccountUserFiles(): UserFile[] {
        let userFiles: UserFile[] = [];
        this.getSortedAuditedAccountViewModel.forEach((auditedAccount) => {
            userFiles.push({
                id: auditedAccount.model.id,
                documentTypeId: "",
                fileName: auditedAccount.model.fileName,
                fileUrl: auditedAccount.model.fileUrl,
            });
        });
        return userFiles;
    }

    @action
    private createViewModels(): void {
        if (this.model.armedForcesProofModels) {
            for (const armedForcesProof of this.model.armedForcesProofModels) {
                this.armedForcesProofViewModels.push(new ArmedForcesProofViewModel(armedForcesProof));
            }
        }
        if (this.model.auditedAccountModels) {
            for (const auditedAccount of this.model.auditedAccountModels) {
                this.auditedAccountViewModels.push(new AuditedAccountViewModel(auditedAccount));
            }
        }
    }

    @action
    public handleEmploymentStatusChange = (): void => {
        // TODO This method should clear any non employment status related data.
        if (this.model.employmentStatus == EmploymentStatusEnum.FullTime) {
            this.model.contractStartDate = null;
            this.model.contractEndDate = null;
            this.model.yearsSelfEmployed = null;
            this.model.monthSelfEmployed = null;
            this.model.canProvideThreeYearsAuditAccounts = false;
        } else if (this.model.employmentStatus == EmploymentStatusEnum.PartTime) {
            this.model.contractStartDate = null;
            this.model.contractEndDate = null;
            this.model.yearsSelfEmployed = null;
            this.model.monthSelfEmployed = null;
        } else if (this.model.employmentStatus == EmploymentStatusEnum.SelfEmployed) {
            this.model.occupation = null;
            this.model.worksFullTimeNotPartTime = false;
            this.model.employerTelephone = null;
            this.model.employerAddressLine1 = null;
            this.model.employerAddressLine2 = null;
            this.model.employerAddressLine3 = null;
            this.model.employerCity = null;
            this.model.employerCounty = null;
            this.model.employerPostcode = null;
            this.model.yearsAtCompany = null;
            this.model.monthsAtCompany = null;
            this.model.onProbation = false;
            this.model.probationLength = null;
            this.model.contractStartDate = null;
            this.model.contractEndDate = null;
        } else {
            this.model.occupation = null;
            this.model.worksFullTimeNotPartTime = false;
            this.model.employerTelephone = null;
            this.model.employerAddressLine1 = null;
            this.model.employerAddressLine2 = null;
            this.model.employerAddressLine3 = null;
            this.model.employerCity = null;
            this.model.employerCounty = null;
            this.model.employerPostcode = null;
            this.model.yearsAtCompany = null;
            this.model.monthsAtCompany = null;
            this.model.onProbation = false;
            this.model.probationLength = null;
            this.model.contractStartDate = null;
            this.model.contractEndDate = null;
            this.model.yearsAtCompany = null;
            this.model.monthsAtCompany = null;
            this.model.onProbation = false;
            this.model.probationLength = null;
            this.model.yearsSelfEmployed = null;
            this.model.monthSelfEmployed = null;
            this.model.canProvideThreeYearsAuditAccounts = false;
        }
    };

    @action
    public handleInputChange = (): void => {
        this.saveStore.setIsSaved(false);
        this.restartTimer();
        this.updateSectionCompleted(FormSectionsEnum.EmploymentDetails, this.canProceed);
    };

    @action
    public handleRadioChange = (fieldName: keyof FieldType<EmploymentDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val == "1" ? true : false);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public setDate(fieldName: keyof FieldType<EmploymentDetailsModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.setValue(fieldName, val);
        }
        this.handleInputChange();
    }

    /**
     * Custom model validation function. Validates child view models.
     * @returns True if model is valid, false if not.
     */
    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;
        if ((await this.isModelValid()) === false) {
            isValid = false;
        }
        for (let i = 0; i < this.armedForcesProofViewModels.length; i++) {
            let armedForcesProof = this.armedForcesProofViewModels[i];
            if ((await armedForcesProof.isModelValid()) === false) {
                isValid = false;
            }
        }
        for (let i = 0; i < this.auditedAccountViewModels.length; i++) {
            let auditedAccount = this.auditedAccountViewModels[i];
            if ((await auditedAccount.isModelValid()) === false) {
                isValid = false;
            }
        }
        return isValid;
    };

    @computed
    public get canProceed(): boolean {
        let completed = true;
        Object.keys(this.model).forEach((_field) => {
            let field = _field as keyof FieldType<EmploymentDetailsModel>;
            let value = this.model.getValue(field);
            if (this.model.employmentStatus == EmploymentStatusEnum.Empty) {
                completed = false;
            }
            if (this.model.employmentStatus == EmploymentStatusEnum.FullTime) {
                if (field == "occupation") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "employerName") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                // SW 14/07/2022 #8523 no longer needed
                // if (field == "employerTelephone") {
                //     if (value) {
                //         if (!validator.isMobilePhone(`${value}`) && !isLandLine(`${value}`)) {
                //             completed = false;
                //         }
                //     }
                // }
                if (field == "employerAddressLine1") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "employerCity") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "employerCounty") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "employerPostcode") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "yearsAtCompany") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "monthsAtCompany") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (this.model.onProbation == true && field == "probationLength") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
            }
            if (this.model.employmentStatus == EmploymentStatusEnum.PartTime) {
                if (field == "contractStartDate") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "contractEndDate") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "occupation") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "employerName") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                // SW 14/07/2022 #8523 no longer needed
                // if (field == "employerTelephone") {
                //     if (this.isEmptyNullOrUndefined(value)) {
                //         completed = false;
                //     }
                // }
                if (field == "employerAddressLine1") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "employerCity") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "employerCounty") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "employerPostcode") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
            }
            if (this.model.employmentStatus == EmploymentStatusEnum.SelfEmployed) {
                if (field == "employerName") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "yearsSelfEmployed") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "monthSelfEmployed") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (this.model.canProvideThreeYearsAuditAccounts == true) {
                    if (this.auditedAccountViewModels.length < 1) {
                        completed = false;
                    }
                }
            }
            if (completed == false) {
                console.log("Cant proceed because: ", field, value);
            }
        });
        if (this.model.regularInArmedForces == true) {
            if (this.armedForcesProofViewModels.length < 1) {
                completed = false;
            }
        }
        return completed;
    }

    public async isFieldValid(fieldName: keyof FieldType<EmploymentDetailsModel>, value: any): Promise<boolean> {
        return await this.isValid(fieldName, value);
    }

    public async isValid(fieldName: keyof FieldType<EmploymentDetailsModel>, value: any, isRequired: boolean = false): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        if (fieldName == "regularInArmedForces") {
            if (value == true && this.armedForcesProofViewModels.length < 1) {
                errorMessage = "provide at least one item of proof";
                isValid = false;
            }
        }
        if (fieldName == "employmentStatus") {
            if (value == EmploymentStatusEnum.Empty && isRequired) {
                errorMessage = "required to proceed";
                isValid = false;
            }
        }
        if (this.model.employmentStatus == EmploymentStatusEnum.FullTime || this.model.employmentStatus == EmploymentStatusEnum.PartTime) {
            if (fieldName == "occupation") {
                if (!validator.isEmpty(value ? value : "") || isRequired) {
                    if (validator.isEmpty(value ? value : "")) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "employerName") {
                if (!validator.isEmpty(value ? value : "") || isRequired) {
                    if (validator.isEmpty(value ? value : "")) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            // SW 14/07/2022 #8523 no longer needed
            // if (fieldName == "employerTelephone") {
            //     if (!validator.isEmpty(value ? value : "") || isRequired) {
            //         if (value) {
            //             if (!validator.isMobilePhone(value) && !isLandLine(value)) {
            //                 errorMessage = "telephone number is not valid";
            //                 isValid = false;
            //             }
            //         }
            //     }
            // }
            if (fieldName == "employerAddressLine1") {
                if (!validator.isEmpty(value ? value : "") || isRequired) {
                    if (validator.isEmpty(value ? value : "")) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "employerCity") {
                if (!validator.isEmpty(value ? value : "") || isRequired) {
                    if (validator.isEmpty(value ? value : "")) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "employerCounty") {
                if (!validator.isEmpty(value ? value : "") || isRequired) {
                    if (validator.isEmpty(value ? value : "")) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "employerPostcode") {
                if (!validator.isEmpty(value ? value : "") || isRequired) {
                    if (validator.isEmpty(value ? value : "")) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                    if (!validator.isPostalCode(value, "GB")) {
                        errorMessage = "not a valid postcode";
                        isValid = false;
                    }
                }
            }
        }
        if (this.model.employmentStatus == EmploymentStatusEnum.PartTime) {
            if (fieldName == "contractStartDate") {
                if (!validator.isEmpty(value ? value : "") || isRequired) {
                    if (validator.isEmpty(value ? value : "")) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "contractEndDate") {
                if (!validator.isEmpty(value ? value : "") || isRequired) {
                    if (validator.isEmpty(value ? value : "")) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
        }
        if (this.model.employmentStatus == EmploymentStatusEnum.SelfEmployed) {
            if (fieldName == "employerName") {
                if (!validator.isEmpty(value ? value : "") || isRequired) {
                    if (validator.isEmpty(value ? value : "")) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
            if (fieldName == "canProvideThreeYearsAuditAccounts") {
                if (value == true && this.auditedAccountViewModels.length < 1) {
                    errorMessage = "provide at least one item of proof";
                    isValid = false;
                }
            }
        }
        if (isValid == false) {
            console.log("Field Not valid: ", fieldName);
        }
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    /**
     * @returns boolean does this section have any errors
     */
    @action
    public async showUnfinishedFields(): Promise<boolean> {
        let hasError = false;
        for (const _field of Object.keys(this.model)) {
            let field = _field as keyof FieldType<EmploymentDetailsModel>;
            let value = this.model.getValue(field);
            let isRequired = true;
            let isValid = await this.isValid(field, value, isRequired);
            if (isValid == false) {
                hasError = true;
            }
        }
        return hasError;
    }
    public beforeUpdate: undefined;
}
